import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  PlusIcon,
  PowerIcon,
  PencilSquareIcon,
  TrashIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import PrestamosParametrizadosService from "../../services/prestamosParametrizadosService";
import PrestamoParametrizadoFormModal from "./PrestamoParametrizadoFormModal";
import ClientesVerModal from "./ClientesVerModal";
import {
  ALCANCES,
  MODOS,
  MODO_LABEL,
  REQUIERE_DISPONIBLE_LABEL,
  extraerLista,
  formatPesos,
  mostrarErrorApi,
  ordenarPrestamos,
  textoDestinatarios,
  textoRangoScore,
} from "./utils";

/** Panel de ayuda: cómo funcionan los préstamos parametrizados. */
function ComoFunciona() {
  return (
    <details className="group rounded-2xl bg-white shadow-sm ring-1 ring-black/5" open>
      <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-gray-700">
        ¿Cómo funciona?{" "}
        <span className="font-normal text-gray-400 group-open:hidden">(ver explicación)</span>
      </summary>
      <div className="grid gap-4 px-5 pb-5 text-xs text-gray-600 md:grid-cols-2">
        <div className="space-y-1.5">
          <p className="font-semibold text-gray-700">Qué es</p>
          <p>
            Un préstamo pre-aprobado que el socio ve en la app, con título y descripción propios. En
            la app ve una sola card de préstamo: si tiene un pre-aprobado se destaca ese, y al
            entrar elige entre el pre-aprobado y el préstamo por disponible (si le corresponde).
          </p>
          <p className="font-semibold text-gray-700 pt-1">A quién se le ofrece (una u otra)</p>
          <p>
            • <b>Por grupo de afinidad</b>: a los socios del grupo, opcionalmente solo a un rango de
            score.
            <br />• <b>A clientes puntuales</b>: solo a la lista cargada (de a uno o con Excel), sin
            importar grupo ni score.
          </p>
          <p className="font-semibold text-gray-700 pt-1">Monto</p>
          <p>
            • <b>Fijo</b>: el socio saca ese monto.
            <br />• <b>Según disponible</b>: el socio elige entre el monto mínimo y su disponible. Siempre
            requiere tener disponible.
          </p>
        </div>
        <div className="space-y-1.5">
          <p className="font-semibold text-gray-700">Cuotas</p>
          <p>
            El socio solo ve las cuotas habilitadas en el préstamo, y de esas solo las que entran en
            su capacidad de pago mensual. Ej: habilitás 12 y 18 y su capacidad le da de 2 a 24 → ve
            12 y 18.
          </p>
          <p className="font-semibold text-gray-700 pt-1">Convivencia con el préstamo por disponible</p>
          <p>
            • <b>Este + disponible</b>: ve los dos. <b>Solo este</b>: se le oculta el préstamo por
            disponible.
            <br />• <b>Requiere disponible</b>: solo se muestra si su disponible supera el monto mínimo.
            <b> Sin disponible</b>: se muestra igual (solo con monto fijo).
          </p>
          <p className="font-semibold text-gray-700 pt-1">Regla de consistencia</p>
          <p>
            Si un mismo socio puede recibir varios préstamos activos (mismo grupo con score
            superpuesto, o clientes en común), todos tienen que tener la misma configuración de
            convivencia. Si no, no se puede guardar ni activar y se indica con cuál choca.
          </p>
        </div>
      </div>
    </details>
  );
}

const th =
  "text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide";

/**
 * PrestamosParametrizadosManager
 *
 * Gestión de los préstamos parametrizados (pre-aprobados) que ve el socio en la app.
 * Misma estructura que AdelantoEfectivoManager: tabla + modal de alta/edición,
 * toggle y eliminación con confirmación.
 */
export default function PrestamosParametrizadosManager() {
  const [prestamos, setPrestamos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingToggleId, setPendingToggleId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  /** null = modal cerrado, {} = crear, préstamo = editar */
  const [modalTarget, setModalTarget] = useState(null);
  /** Préstamo cuyos clientes se ven en modo solo lectura (null = cerrado) */
  const [verClientesDe, setVerClientesDe] = useState(null);

  // ── Carga ────────────────────────────────────────────────────────────────
  const loadPrestamos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await PrestamosParametrizadosService.getAll();
      setPrestamos(
        extraerLista(res).map((p) => ({
          ...p,
          activo: p.activo === 1 || p.activo === true,
          cuotas: Array.isArray(p.cuotas) ? p.cuotas : [],
        })),
      );
    } catch {
      setError("No se pudieron cargar los préstamos parametrizados.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrestamos();
  }, [loadPrestamos]);

  const handleModalSaved = () => {
    setModalTarget(null);
    loadPrestamos();
  };

  // ── Toggle activo/inactivo ───────────────────────────────────────────────
  const handleToggle = async (prestamo) => {
    const activar = !prestamo.activo;
    const { isConfirmed } = await Swal.fire({
      title: activar ? "¿Activar préstamo?" : "¿Desactivar préstamo?",
      text: activar
        ? `"${prestamo.titulo}" se va a mostrar en la app a los socios que correspondan.`
        : `"${prestamo.titulo}" deja de mostrarse en la app.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: activar ? "Activar" : "Desactivar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: activar ? "#56C401" : "#e53e3e",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
    });
    if (!isConfirmed) return;

    setPendingToggleId(prestamo.id);
    try {
      await PrestamosParametrizadosService.toggle(prestamo.id);
      setPrestamos((prev) =>
        prev.map((p) => (p.id === prestamo.id ? { ...p, activo: activar } : p)),
      );
    } catch (err) {
      // Al activar puede volver 409 por conflicto de configuración
      mostrarErrorApi(err, "No se pudo cambiar el estado del préstamo.");
    } finally {
      setPendingToggleId(null);
    }
  };

  // ── Eliminación ──────────────────────────────────────────────────────────
  const handleEliminar = async (prestamo) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar préstamo?",
      text: `"${prestamo.titulo}" se elimina con sus cuotas. Esta acción es permanente.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
    });
    if (!isConfirmed) return;

    setPendingDeleteId(prestamo.id);
    try {
      await PrestamosParametrizadosService.remove(prestamo.id);
      setPrestamos((prev) => prev.filter((p) => p.id !== prestamo.id));
      Swal.fire({
        icon: "success",
        title: "Préstamo eliminado",
        position: "top-end",
        toast: true,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (err) {
      mostrarErrorApi(err, "No se pudo eliminar el préstamo.");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const lista = ordenarPrestamos(prestamos);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <ComoFunciona />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Parámetros de Préstamos
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Préstamos pre-aprobados que ve el socio en la app, por grupo de
            afinidad y score o para clientes puntuales.
          </p>
        </div>
        <button
          onClick={() => setModalTarget({})}
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-custom-green-ccard-1 text-white text-sm font-medium hover:bg-custom-green-ccard-2 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Nuevo préstamo
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
            <div className="h-7 w-7 rounded-full border-4 border-custom-green-ccard-1 border-t-transparent animate-spin" />
            <span className="text-sm">Cargando préstamos...</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <p className="text-red-500 text-sm">{error}</p>
            <button
              onClick={loadPrestamos}
              className="text-sm text-custom-green-ccard-1 underline hover:no-underline"
            >
              Reintentar
            </button>
          </div>
        )}

        {!loading && !error && lista.length === 0 && (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
            <p className="text-sm">No hay préstamos parametrizados.</p>
            <button
              onClick={() => setModalTarget({})}
              className="text-sm text-custom-green-ccard-1 underline hover:no-underline"
            >
              Crear el primero
            </button>
          </div>
        )}

        {!loading && !error && lista.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className={th}>Préstamo</th>
                  <th className={th}>A quién</th>
                  <th className={th}>Score</th>
                  <th className={th}>Monto</th>
                  <th className={th}>Plan / Cuotas</th>
                  <th className={th}>Se muestra</th>
                  <th className={th}>Estado</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {lista.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 max-w-xs">
                      <div className="font-medium text-gray-800">{p.titulo}</div>
                      {!!p.descripcion && (
                        <div
                          className="text-xs text-gray-400 truncate"
                          title={p.descripcion}
                        >
                          {p.descripcion}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {p.alcance === ALCANCES.CLIENTES ? (
                        <button
                          type="button"
                          onClick={() => setVerClientesDe(p)}
                          title="Ver clientes"
                          className="text-left text-custom-green-ccard-1 underline decoration-dotted hover:decoration-solid"
                        >
                          {textoDestinatarios(p)}
                        </button>
                      ) : (
                        <div>{textoDestinatarios(p)}</div>
                      )}
                      <div className="text-xs text-gray-400">
                        {p.alcance === ALCANCES.CLIENTES ? "Clientes puntuales" : "Grupo de afinidad"}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {p.alcance === ALCANCES.CLIENTES
                        ? "—"
                        : textoRangoScore(p.scoreMin, p.scoreMax)}
                    </td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                      {p.monto == null ? (
                        <span className="text-xs font-medium text-indigo-600">Según disponible</span>
                      ) : (
                        formatPesos(p.monto)
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      <div className="text-xs font-medium text-gray-700">
                        {p.plan?.nombre ?? `Plan ${p.idPlan}`}
                      </div>
                      {p.cuotas.length > 0 && (
                        <div className="text-xs text-gray-400">
                          {p.cuotas.join(", ")} cuotas
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex w-fit px-2 py-0.5 rounded-full text-xs font-medium ${
                            p.modo === MODOS.SOLO_PARAMETRIZADO
                              ? "bg-amber-100 text-amber-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {MODO_LABEL[p.modo] ?? p.modo}
                        </span>
                        <span
                          className={`inline-flex w-fit px-2 py-0.5 rounded-full text-xs font-medium ${
                            p.requiereDisponible === "N"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {REQUIERE_DISPONIBLE_LABEL[p.requiereDisponible] ??
                            p.requiereDisponible}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {p.activo ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {p.alcance === ALCANCES.CLIENTES && (
                          <button
                            onClick={() => setVerClientesDe(p)}
                            title="Ver clientes"
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                          >
                            <UsersIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggle(p)}
                          disabled={pendingToggleId === p.id}
                          title={p.activo ? "Desactivar" : "Activar"}
                          className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            p.activo
                              ? "text-green-600 hover:bg-green-50"
                              : "text-gray-400 hover:bg-gray-100"
                          }`}
                        >
                          <PowerIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setModalTarget(p)}
                          title="Editar"
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEliminar(p)}
                          disabled={pendingDeleteId === p.id}
                          title="Eliminar"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && (
        <p className="text-xs text-gray-400 text-right">
          {lista.length} préstamo{lista.length !== 1 ? "s" : ""} configurado
          {lista.length !== 1 ? "s" : ""}
        </p>
      )}

      {verClientesDe && (
        <ClientesVerModal
          prestamo={verClientesDe}
          onClose={() => setVerClientesDe(null)}
        />
      )}

      {modalTarget !== null && (
        <PrestamoParametrizadoFormModal
          prestamo={Object.keys(modalTarget).length > 0 ? modalTarget : null}
          onClose={() => setModalTarget(null)}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}
